import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  CustomerServiceRepCreateBody,
  CustomerServiceRepCreateResponse,
  CustomerServiceRepDeleteResponse,
  CustomerServiceRepReassignBody,
  CustomerServiceRepReassignResponse,
  CustomerServiceRepUpdateBody,
  CustomerServiceRepUpdateResponse,
  createCustomerServiceRep,
  deleteCustomerServiceRep,
  reassignCustomerServiceRep,
  updateCustomerServiceRep,
} from '~/services/LuxLoyaltyCustomerServiceRepService';

import {
  customerServiceRepAssignmentsQueryKeys,
  customerServiceRepQueryKeys,
} from './customerServiceRepQueryKeys';

interface MutationOptions<TData> {
  onSuccess?: (data: TData) => void;
  onError?: (error: Error) => void;
}

/** Create a customer service rep. */
export function useCreateCustomerServiceRep(options: MutationOptions<CustomerServiceRepCreateResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerServiceRepCreateBody) => createCustomerServiceRep(body),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: customerServiceRepQueryKeys.lists() });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
}

/** Update a customer service rep. */
export function useUpdateCustomerServiceRep(options: MutationOptions<CustomerServiceRepUpdateResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CustomerServiceRepUpdateBody }) =>
      updateCustomerServiceRep(id, body),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: customerServiceRepQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerServiceRepQueryKeys.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: customerServiceRepAssignmentsQueryKeys.all });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
}

/** Delete a customer service rep. */
export function useDeleteCustomerServiceRep(options: MutationOptions<CustomerServiceRepDeleteResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCustomerServiceRep(id),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: customerServiceRepQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerServiceRepAssignmentsQueryKeys.all });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
}

/** Bulk reassign accounts from one rep to another. */
export function useReassignCustomerServiceRep(options: MutationOptions<CustomerServiceRepReassignResponse> = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerServiceRepReassignBody) => reassignCustomerServiceRep(body),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: customerServiceRepAssignmentsQueryKeys.all });
      options.onSuccess?.(data);
    },
    onError: options.onError,
  });
}
